<template>
  <!--
    AB#21909 — "Was this read right?" 👍 is one tap. 👎 opens a small panel:
    a reason and a mandatory line of text, because a bare thumbs-down
    teaches nobody anything. Shows the person's own verdict on the current
    reading; a regenerated read starts blank.
  -->
  <div
    class="rv"
    :class="[`rv--${size}`, { 'rv--open': open }]"
    data-test="reading-verdict"
    @click.stop
  >
    <span v-if="size === 'md'" class="rv-q">{{ copy.question }}</span>
    <button
      type="button"
      class="rv-btn"
      :class="{ on: current?.verdict === 'right' }"
      :disabled="busy"
      :title="copy.yes"
      data-test="rv-right"
      @click="right"
    >
      👍<span v-if="size === 'md'"> Yes</span>
    </button>
    <button
      type="button"
      class="rv-btn"
      :class="{ on: current?.verdict === 'wrong' }"
      :disabled="busy"
      :title="copy.no"
      data-test="rv-wrong"
      @click="open = !open"
    >
      👎<span v-if="size === 'md'"> No</span>
    </button>
    <span v-if="current?.verdict === 'wrong' && !open" class="rv-said" :title="current.note ?? ''">
      {{ reasonLabel(current.reason) }}
    </span>

    <div v-if="open" class="rv-panel" data-test="rv-panel">
      <div class="rv-panel-t">{{ copy.panel }}</div>
      <div class="rv-reasons">
        <button
          v-for="r in reasons"
          :key="r.value"
          type="button"
          class="rv-chip"
          :class="{ on: reason === r.value }"
          :data-test="`rv-reason-${r.value}`"
          @click="reason = r.value"
        >
          {{ r.label }}
        </button>
      </div>
      <textarea
        v-model="note"
        class="rv-note"
        rows="2"
        maxlength="1000"
        :placeholder="copy.placeholder"
        data-test="rv-note"
        @keydown.enter.ctrl.prevent="save"
        @keydown.enter.meta.prevent="save"
      ></textarea>
      <div class="rv-panel-f">
        <span class="rv-hint" :class="{ bad: touched && !valid }">
          {{
            !reason
              ? 'Pick a reason'
              : note.trim().length < MIN_NOTE
                ? `A few words (${MIN_NOTE}+ characters)`
                : 'Goes to Setup → Agents → Outcomes'
          }}
        </span>
        <button type="button" class="dash-btn" data-test="rv-cancel" @click="open = false">
          Cancel
        </button>
        <button
          type="button"
          class="dash-btn dash-btn--primary"
          :disabled="!valid || busy"
          data-test="rv-save"
          @click="save"
        >
          {{ busy ? 'Saving…' : copy.send }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { showToast } from '@/composables/swal';
import {
  MIN_NOTE,
  reasonLabel,
  reasonsFor,
  sendReadingVerdict,
  type MyReadingFeedback,
  type ReadingReason,
  type ReadingSource,
} from '@/composables/useReadingFeedback';

const props = withDefaults(
  defineProps<{
    orderItemId: number;
    initial?: MyReadingFeedback | null;
    source: ReadingSource;
    size?: 'sm' | 'md';
  }>(),
  { initial: null, size: 'md' }
);
const emit = defineEmits<{ (e: 'saved', feedback: MyReadingFeedback): void }>();

// AB#21998 — on a finance card the question is about the card, not about
// ELSA: it comes from the invoice ledger, and what can be wrong with it is
// different (already paid, not due, should not be invoiced).
const reasons = computed(() => reasonsFor(props.source));
const copy = computed(() =>
  props.source === 'finance_queue'
    ? {
        question: 'Is this right?',
        yes: 'Yes, this is right',
        no: 'No, this is wrong',
        panel: "What's wrong with this card?",
        placeholder: 'What do you know that the invoice record does not? (required)',
        send: 'Send',
      }
    : {
        question: 'Was this read right?',
        yes: 'Yes, this read is right',
        no: 'No, this read is wrong',
        panel: "What's wrong with this read?",
        placeholder: 'What should ELSA have seen? (required)',
        send: 'Send to ELSA',
      }
);

const current = ref<MyReadingFeedback | null>(props.initial ?? null);
watch(
  () => props.initial,
  (v) => (current.value = v ?? null)
);
const open = ref(false);
const busy = ref(false);
const touched = ref(false);
const reason = ref<ReadingReason | null>(current.value?.reason ?? null);
const note = ref(current.value?.note ?? '');
const valid = computed(() => !!reason.value && note.value.trim().length >= MIN_NOTE);

const right = async (): Promise<void> => {
  open.value = false;
  busy.value = true;
  const r = await sendReadingVerdict(props.orderItemId, 'right', props.source);
  busy.value = false;
  if (r.ok) {
    current.value = r.feedback ?? { verdict: 'right', reason: null, note: null };
    emit('saved', current.value);
  } else showToast(r.message, 'error');
};
const save = async (): Promise<void> => {
  touched.value = true;
  if (!valid.value) return;
  busy.value = true;
  const r = await sendReadingVerdict(
    props.orderItemId,
    'wrong',
    props.source,
    reason.value,
    note.value.trim()
  );
  busy.value = false;
  if (r.ok) {
    current.value = r.feedback ?? {
      verdict: 'wrong',
      reason: reason.value,
      note: note.value.trim(),
    };
    open.value = false;
    showToast(r.message || 'Noted — thank you.', 'success');
    emit('saved', current.value);
  } else showToast(r.message, 'error');
};
</script>

<style scoped>
.rv {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: var(--ink-soft);
}
.rv-q {
  margin-right: 2px;
}
.rv-btn {
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  border-radius: 8px;
  padding: 3px 8px;
  font-size: 13px;
  cursor: pointer;
  line-height: 1.3;
}
.rv--sm .rv-btn {
  padding: 1px 6px;
  font-size: 12px;
}
.rv-btn:hover {
  background: var(--surface-2);
}
.rv-btn.on {
  border-color: #7c5cd6;
  box-shadow: inset 0 0 0 1px #7c5cd6;
}
.rv-said {
  font-size: 11.5px;
  color: var(--warning);
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rv-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 30;
  width: min(92vw, 380px);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: var(--shadow-2);
  padding: 12px;
  text-align: left;
  cursor: default;
}
.rv-panel-t {
  font-weight: 700;
  color: var(--ink);
  font-size: 13px;
  margin-bottom: 8px;
}
.rv-reasons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
.rv-chip {
  border: 1px solid var(--border);
  background: var(--surface-2);
  color: var(--ink-soft);
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 12px;
  cursor: pointer;
}
.rv-chip.on {
  background: #7c5cd6;
  border-color: #7c5cd6;
  color: #fff;
}
.rv-note {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--ink);
  padding: 7px 9px;
  font-size: 13px;
  resize: vertical;
  font-family: inherit;
}
.rv-panel-f {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
.rv--sm .rv-panel {
  left: auto;
  right: 0;
}
.rv-hint {
  flex: 1;
  font-size: 11.5px;
  color: var(--ink-mute);
}
.rv-hint.bad {
  color: var(--danger);
}
</style>
